import React, { useState } from 'react';

interface ContactStepProps {
  onNext: () => void;
  onPrev: () => void;
  projectDetails?: any;
  selectedPackage?: string;
  timeline?: string;
  addons?: any;
  totalPrice?: number;
}

const ContactStep: React.FC<ContactStepProps> = ({
  onNext,
  onPrev,
  projectDetails,
  selectedPackage,
  timeline,
  addons,
  totalPrice
}) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setContactInfo({
      ...contactInfo,
      [field]: value,
    });
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate fields
    const newErrors = {
      name: '',
      email: '',
    };

    if (!contactInfo.name.trim()) {
      newErrors.name = 'Naam is verplicht';
    }

    if (!contactInfo.email.trim()) {
      newErrors.email = 'Email is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      newErrors.email = 'Voer een geldig emailadres in';
    }

    // If there are errors, show them and stop
    if (newErrors.name || newErrors.email) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Send data to Netlify Function
      const response = await fetch('/.netlify/functions/submit-wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactInfo,
          projectDetails,
          selectedPackage,
          timeline,
          addons,
          totalPrice
        })
      });

      const data = await response.json();

      console.log('Response status:', response.status);
      console.log('Response data:', data);

      if (response.ok && data.success) {
        // Success! Move to next step
        onNext();
      } else {
        // Handle error
        console.error('Submission failed:', data);
        setSubmitError(data.error || data.message || 'Er is iets misgegaan. Probeer het opnieuw.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError('Kan geen verbinding maken met de server. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Dark Header Section */}
      <div className="bg-black/70 backdrop-blur-sm py-8 mb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Progress Steps */}
            <div className="flex items-center justify-center">
              <div className="flex items-center flex-wrap md:flex-nowrap justify-center gap-2 md:gap-0">
                {/* Step 1 - Completed */}
                <div className="flex items-center">
                  <div className="text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                    style={{
                      background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                    }}>
                    ✓
                  </div>
                  <span className="ml-2 text-white text-sm md:text-base whitespace-nowrap">Stap 1</span>
                </div>

                <div className="hidden md:block w-8 lg:w-12 h-0.5 bg-white/30 mx-2"></div>

                {/* Step 2 - Completed */}
                <div className="flex items-center">
                  <div className="text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                    style={{
                      background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                    }}>
                    ✓
                  </div>
                  <span className="ml-2 text-white text-sm md:text-base whitespace-nowrap">Stap 2</span>
                </div>

                <div className="hidden md:block w-8 lg:w-12 h-0.5 bg-white/30 mx-2"></div>

                {/* Step 3 - Current */}
                <div className="flex items-center">
                  <div className="bg-blue-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <span className="ml-2 text-white font-semibold text-sm md:text-base whitespace-nowrap">Stap 3</span>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-8 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl">
          <h2 className="text-3xl font-bold text-blue-800 mb-2 text-center">
            Bijna klaar!
          </h2>
          <p className="text-gray-600 mb-8 text-center">
            Vul je contactgegevens in en ontvang binnen 24 uur een offerte op maat.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Naam *
                </label>
                <input
                  type="text"
                  value={contactInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.name
                      ? 'border-orange-500 focus:ring-orange-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="Volledige naam"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-orange-600 font-medium">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.email
                      ? 'border-orange-500 focus:ring-orange-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="E-mailadres"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-orange-600 font-medium">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Telefoon
                </label>
                <input
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Telefoonnummer"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Bedrijf
                </label>
                <input
                  type="text"
                  value={contactInfo.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bedrijfsnaam"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Nadere toelichting (optioneel)
              </label>
              <textarea
                value={contactInfo.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none"
                placeholder="Nadere toelichting (optioneel)"
              />
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Wat gebeurt er na het versturen?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>We nemen binnen 24 uur contact met je op</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>We plannen een vrijblijvend gesprek in</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Na akkoord starten we direct met je project</span>
                </li>
              </ul>
            </div>

            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
                {submitError}
              </div>
            )}

            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onPrev}
                disabled={isSubmitting}
                className="text-blue-600 hover:text-blue-700 font-semibold flex items-center disabled:opacity-50"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Vorige stap
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="text-white px-8 py-3 rounded-lg font-semibold text-lg transition transform hover:scale-105 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isSubmitting ? '#9ca3af' : 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                }}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Versturen...
                  </>
                ) : (
                  <>
                    Verstuur Aanvraag
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactStep;